import { useState, useRef, useEffect } from 'react';
import { toPersian } from '../../utils/persianNumber';
import {
  JALALI_WEEKDAYS, buildJalaliMonthGrid, jalaliLabel,
  addDays, startOfDay, isSameDay, isWithin, toJalali,
} from '../../utils/jalali';

const BLUE = '#29AAE1';
const BLUE_SOFT = '#EDF3FC';
const BLUE_MID = '#B7C9EC';

const fmt = (d) => {
  if (!d) return '';
  const { jy, jm, jd } = toJalali(d);
  return toPersian(`${jy}/${String(jm).padStart(2, '0')}/${String(jd).padStart(2, '0')}`);
};

const PRESETS = [
  { label: 'امروز', range: () => { const t = startOfDay(new Date()); return [t, t]; } },
  { label: '۷ روز اخیر', range: () => { const t = startOfDay(new Date()); return [addDays(t, -6), t]; } },
  { label: '۳۰ روز اخیر', range: () => { const t = startOfDay(new Date()); return [addDays(t, -29), t]; } },
  { label: 'این ماه', range: () => {
      const t = startOfDay(new Date());
      const grid = buildJalaliMonthGrid(t);
      return [grid.firstDate, t];
    } },
];

// Interactive Jalali (Persian) calendar - pick a start and end day to form
// an interval, light-blue themed with hover/selection motion. Self-
// contained (no npm date-picker dependency, matching this project's
// convention) - month grid math lives in utils/jalali.js.
export default function JalaliDateRangePicker({ startDate, endDate, onChange }) {
  const [open, setOpen] = useState(false);
  const [viewAnchor, setViewAnchor] = useState(endDate || startDate || new Date());
  const [pendingStart, setPendingStart] = useState(startDate || null);
  const [hoverDate, setHoverDate] = useState(null);
  const rootRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  useEffect(() => {
    if (open) { setPendingStart(startDate || null); setViewAnchor(endDate || startDate || new Date()); }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const grid = buildJalaliMonthGrid(viewAnchor);

  const goMonth = (delta) => {
    setViewAnchor(delta > 0 ? addDays(grid.lastDate, 1) : addDays(grid.firstDate, -1));
  };

  const pickDay = (date) => {
    if (!pendingStart || (startDate && endDate)) {
      setPendingStart(date);
      onChange?.(date, null);
    } else {
      const [s, e] = date < pendingStart ? [date, pendingStart] : [pendingStart, date];
      onChange?.(s, e);
      setPendingStart(null);
      setOpen(false);
    }
  };

  const applyPreset = (preset) => {
    const [s, e] = preset.range();
    onChange?.(s, e);
    setPendingStart(null);
    setViewAnchor(e);
    setOpen(false);
  };

  const previewEnd = pendingStart && !endDate ? hoverDate : null;

  return (
    <div ref={rootRef} style={{ position: 'relative', fontFamily: 'inherit' }}>
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '9px 14px',
          border: `1.5px solid ${open ? BLUE : '#e2e8f0'}`, borderRadius: 10,
          background: open ? BLUE_SOFT : '#fff', cursor: 'pointer', fontFamily: 'inherit',
          fontSize: 13, color: '#334155', transition: 'all 0.18s ease', minWidth: 220,
        }}
      >
        <span style={{ fontSize: 15 }}>📅</span>
        <span style={{ fontWeight: 600 }}>
          {startDate ? fmt(startDate) : 'از تاریخ'} <span style={{ color: '#94a3b8', margin: '0 4px' }}>تا</span> {endDate ? fmt(endDate) : 'تا تاریخ'}
        </span>
      </button>

      {open && (
        <div
          className="fade-in"
          style={{
            position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 50,
            background: '#fff', borderRadius: 16, border: `1px solid ${BLUE_MID}`,
            boxShadow: '0 20px 48px rgba(35,58,122,0.18)', padding: 18, width: 300,
          }}
        >
          {/* Presets */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
            {PRESETS.map((p) => (
              <button key={p.label} onClick={() => applyPreset(p)}
                style={{
                  padding: '5px 10px', borderRadius: 20, border: `1px solid ${BLUE_MID}`,
                  background: BLUE_SOFT, color: BLUE, fontSize: 11, fontWeight: 600,
                  cursor: 'pointer', fontFamily: 'inherit', transition: 'transform 0.12s ease, background 0.12s ease',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = BLUE_MID; e.currentTarget.style.transform = 'scale(1.05)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = BLUE_SOFT; e.currentTarget.style.transform = 'scale(1)'; }}
              >{p.label}</button>
            ))}
          </div>

          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <button onClick={() => goMonth(-1)} aria-label="ماه قبل" style={navBtnStyle}>‹</button>
            <span key={`${grid.jy}-${grid.jm}`} className="fade-in" style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>
              {jalaliLabel(grid.jy, grid.jm)}
            </span>
            <button onClick={() => goMonth(1)} aria-label="ماه بعد" style={navBtnStyle}>›</button>
          </div>

          {/* Weekday row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', marginBottom: 4 }}>
            {JALALI_WEEKDAYS.map((w) => (
              <div key={w} style={{ textAlign: 'center', fontSize: 10.5, fontWeight: 700, color: '#94a3b8', padding: '4px 0' }}>{w}</div>
            ))}
          </div>

          {/* Day grid */}
          <div key={`grid-${grid.jy}-${grid.jm}`} className="fade-in" style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {grid.weeks.map((week, wi) => (
              <div key={wi} style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)' }}>
                {week.map(({ date, jd, inMonth }) => {
                  const isStart = isSameDay(date, startDate) || isSameDay(date, pendingStart);
                  const isEnd = isSameDay(date, endDate);
                  const inRange = isWithin(date, startDate || pendingStart, endDate || previewEnd);
                  const isToday = isSameDay(date, startOfDay(new Date()));
                  const edge = isStart || isEnd;
                  return (
                    <button
                      key={date.toISOString()}
                      onClick={() => pickDay(startOfDay(date))}
                      onMouseEnter={() => setHoverDate(startOfDay(date))}
                      disabled={!inMonth}
                      style={{
                        position: 'relative', height: 32, border: 'none', background: 'none',
                        cursor: inMonth ? 'pointer' : 'default', fontFamily: 'inherit',
                        fontSize: 12.5, color: !inMonth ? '#cbd5e1' : edge ? '#fff' : '#334155',
                        fontWeight: edge ? 700 : isToday ? 700 : 500,
                        zIndex: 1, transition: 'transform 0.12s ease',
                      }}
                      onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.88)'; }}
                      onMouseUp={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                    >
                      {/* range connector background */}
                      {inMonth && inRange && !edge && (
                        <span style={{ position: 'absolute', inset: '2px 0', background: BLUE_SOFT, zIndex: -1 }} />
                      )}
                      {inMonth && edge && (
                        <span style={{
                          position: 'absolute', inset: '2px 4px', borderRadius: 999, background: BLUE,
                          zIndex: -1, boxShadow: '0 4px 10px rgba(35,58,122,0.4)', transition: 'transform 0.15s ease',
                        }} />
                      )}
                      {inMonth && isToday && !edge && (
                        <span style={{ position: 'absolute', bottom: 3, left: '50%', transform: 'translateX(-50%)', width: 4, height: 4, borderRadius: '50%', background: BLUE }} />
                      )}
                      {toPersian(jd)}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>

          <div style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 11, color: '#94a3b8' }}>
              {pendingStart && !endDate ? 'تاریخ پایان را انتخاب کنید' : 'یک بازه انتخاب کنید'}
            </span>
            <button onClick={() => { onChange?.(null, null); setPendingStart(null); }} style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' }}>پاک کردن</button>
          </div>
        </div>
      )}
    </div>
  );
}

const navBtnStyle = {
  width: 28, height: 28, borderRadius: 8, border: `1px solid ${BLUE_MID}`,
  background: BLUE_SOFT, color: BLUE, fontSize: 16, fontWeight: 700,
  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
  fontFamily: 'inherit', transition: 'background 0.15s ease, transform 0.15s ease',
};
